import { useContext, useEffect, useRef, useState } from "react";
import Spinner from "../../components/spinner/spinner";
import { Toast } from "primereact/toast";
import {
  automaticStatusOptions,
  processingStatusOptions,
  SwitchboardCustomerOptions
} from "../../constants/constants";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import * as actionCreators from "../../store/action-creators";
import { Form, Formik } from "formik";
import styles from "./RegionMapping.module.scss";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import {
  approveRegionMappings,
  autoToggleRegionMapping,
  createRegionMapping,
  deleteRegionMappings,
  getRegionMappings
} from "../../services/RegionMappingService";
import { DataTable } from "primereact/datatable";
import * as constants from "../../constants/constants";
import { regionMappingColumns } from "../../constants/table-columns/tableColumns";
import { Column } from "primereact/column";
import { format, parseISO } from "date-fns";
import { Tag } from "primereact/tag";
import { processingStatusKeyValueOptions } from "../../constants/constants";
import { InputSwitch } from "primereact/inputswitch";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import FeatureFlagDataContext from "../../store/featureFlags";

const RegionMapping = () => {
  const [featureFlags, setFeatureFlags] = useContext(FeatureFlagDataContext);
  const toast = useRef(null);
  const toastTopRight = useRef(null);
  const formRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageRows, setCurrentPageRows] = useState(10);
  const [disableFields, setDisableFields] = useState(false);
  const [currentFilters, setCurrentFilters] = useState();
  const [showSpinner, setShowSpinner] = useState(false);
  const [regionMappings, setRegionMappings] = useState([]);
  const [selectedRegionMappings, setSelectedRegionMappings] = useState([]);
  const [
    showApproveRegionMappingsConfirmDialog,
    setShowApproveRegionMappingsConfirmDialog
  ] = useState(false);
  const [approveRegionMappingsIds, setApproveRegionMappingsIds] = useState([]);
  const [
    showDeleteRegionMappingsConfirmDialog,
    setShowDeleteRegionMappingsConfirmDialog
  ] = useState(false);
  const [deleteRegionMappingsIds, setDeleteRegionMappingsIds] = useState([]);
  const [showCreateRegionMappingsDialog, setShowCreateRegionMappingsDialog] =
    useState(false);
  const createRegionMappingFormRef = useRef(null);

  const tradingPartnersFilterDropdownList = [
    ...(featureFlags?.enableWalmartRegionMappings
      ? [
          {
            label: "Walmart",
            value: SwitchboardCustomerOptions.WalmartIntercompany
          }
        ]
      : []),
    ...(featureFlags?.enableSamsClubRegionMappings
      ? [{ label: "Sam's Club", value: SwitchboardCustomerOptions.SamsClub }]
      : []),
    ...(featureFlags?.enableTraderJoesRegionMappings
      ? [{ label: "Trader Joe's", value: SwitchboardCustomerOptions.TraderJoes }]
      : [])
  ];
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const dispatch = useDispatch();
  const { getWalmartRegions } = bindActionCreators(actionCreators, dispatch);
  const regionsFilterDropdownList = useSelector((state) => {
    const data = state.orderReducer.walmartRegions;

    const transformedRegions = data.map((region) => {
      return {
        label: region.regionName + " (" + region.regionCode + ")",
        value: region.regionCode
      };
    });

    return transformedRegions;
  });

  const processingStatusFilterDropdownList = [
    { label: "All", value: processingStatusOptions.All },
    { label: "Pending", value: processingStatusOptions.Pending },
    { label: "Approved", value: processingStatusOptions.Approved }
  ];

  const automaticStatusFilterDropdownList = [
    { label: "All", value: null },
    { label: "Automatic", value: automaticStatusOptions.Automatic },
    { label: "Manual", value: automaticStatusOptions.Manual }
  ];

  const initialValues = {
    tradingPartner: featureFlags?.enableWalmartRegionMappings
      ? SwitchboardCustomerOptions.WalmartIntercompany
      : featureFlags?.enableSamsClubRegionMappings
      ? SwitchboardCustomerOptions.SamsClub
      : featureFlags?.enableTraderJoesRegionMappings
      ? SwitchboardCustomerOptions.TraderJoes
      : null,
    vendorSequenceNumber: null,
    dcNumber: null,
    regionCode: null,
    regionMappingStatus: processingStatusOptions.Approved,
    isAutomated: automaticStatusOptions.Manual
  };

  const initialValuesForCreateRegionMapping = {
    tradingPartner: featureFlags?.enableWalmartRegionMappings
      ? SwitchboardCustomerOptions.WalmartIntercompany
      : featureFlags?.enableSamsClubRegionMappings
      ? SwitchboardCustomerOptions.SamsClub
      : featureFlags?.enableTraderJoesRegionMappings
      ? SwitchboardCustomerOptions.TraderJoes
      : null,
    vendorSequenceNumber: null,
    dcNumber: null,
    regionCode: null
  };

  const [
    validationCheckForCreateRegionMapping,
    setValidationCheckForCreateRegionMapping
  ] = useState({
    tradingPartner: false,
    vendorSequenceNumber: false,
    regionCode: false
  });

  useEffect(() => {
    const params = {
      tradingPartner: formRef?.current?.values?.tradingPartner
    };
    getWalmartRegions({ params });

    fetchRegionMappings(initialValues);
  }, []);

  function formatDateTime(isoString) {
    const date = parseISO(isoString);
    return format(date, "yyyy-MM-dd");
  }

  const fetchRegionMappings = async (values) => {
    try {
      setShowSpinner(true);

      values = {
        tradingPartner: values?.tradingPartner,
        vendorSequenceNumber: values?.vendorSequenceNumber,
        dcNumber: values?.dcNumber,
        regionCode: values?.regionCode,
        regionMappingStatus: values?.regionMappingStatus,
        isAutomated:
          values?.isAutomated === automaticStatusOptions.Automatic
            ? true
            : values?.isAutomated === automaticStatusOptions.Manual
            ? false
            : null
      };

      setCurrentFilters(values);

      let result = await getRegionMappings(values);

      setRegionMappings(result);
      setSelectedRegionMappings([]);

      setShowSpinner(false);
    } catch (error) {
      setShowSpinner(false);

      setRegionMappings([]);
      setSelectedRegionMappings([]);

      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.message,
        life: 4000
      });
    }
  };

  const formatProcessingStatusTemplate = (rowData) => {
    let severityValue = "";

    switch (rowData.regionSelectionStatus) {
      case processingStatusOptions.Pending:
        severityValue = "secondary";
        break;
      case processingStatusOptions.Approved:
        severityValue = "success";
        break;
      default:
        severityValue = "";
    }

    return (
      <div>
        <Tag
          severity={severityValue}
          value={processingStatusKeyValueOptions[rowData.regionSelectionStatus]}
          rounded
          style={{ marginTop: "0.2rem", marginBottom: "0.2rem" }}
        ></Tag>
      </div>
    );
  };

  const onAutomaticStatusChange = async (rowData) => {
    let response;

    try {
      setShowSpinner(true);

      const params = {
        regionMappingId: rowData.regionMappingId,
        isAutomated: rowData.isAutomated === false ? true : false
      };

      response = await autoToggleRegionMapping(params);

      setShowSpinner(false);
    } catch (error) {
      setShowSpinner(false);
      toast.current.show({
        severity: "error",
        summary: "Auto Toggle Failed",
        detail: error.message,
        life: 3000
      });
    }

    setShowSpinner(true);

    const result = await getRegionMappings(currentFilters);
    setRegionMappings(result);
    setSelectedRegionMappings([]);

    setShowSpinner(false);

    if (response?.statusCode === 200) {
      toastTopRight.current.show({
        severity: "success",
        summary: "Success",
        detail:
          rowData.isAutomated === false
            ? "Region Mapping set to Automatic"
            : "Region Mapping set to Manual",
        life: 3000
      });
    } else if (response?.statusCode) {
      toast.current.show({
        severity: "error",
        summary: "Auto Toggle Failed",
        detail: response?.result,
        life: 3000
      });
    }
  };

  const formatAutomaticStatusTemplate = (rowData) => {
    return (
      <div>
        <InputSwitch
          checked={rowData.isAutomated}
          onChange={(e) => onAutomaticStatusChange(rowData)}
          style={{
            width: "3.2rem",
            height: "1.6rem"
          }}
          tooltip={rowData.isAutomated === true ? "Automatic" : "Manual"}
        ></InputSwitch>
      </div>
    );
  };

  const formatAprrovedDateUTCTemplate = (rowData) => {
    return rowData?.approvedDateUTC
      ? formatDateTime(rowData?.approvedDateUTC)
      : null;
  };

  const approveRegionMappingsHandler = () => {
    setShowSpinner(true);

    if (selectedRegionMappings.length === 0) {
      setShowSpinner(false);

      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail:
          "Please select at least one Pending region mapping to be approved.",
        life: 3000
      });

      return;
    }

    setApproveRegionMappingsIds([]);

    for (const regionMapping of selectedRegionMappings) {
      if (
        regionMapping?.regionSelectionStatus ===
        processingStatusOptions.Approved
      ) {
        setShowSpinner(false);
        toast.current.show({
          severity: "warn",
          summary: "Warning",
          detail: "Only Pending region mappings can be approved.",
          life: 3000
        });
        return;
      } else {
        setApproveRegionMappingsIds((prev) => [
          ...prev,
          regionMapping?.regionMappingId
        ]);
      }
    }

    setShowApproveRegionMappingsConfirmDialog(true);

    setShowSpinner(false);
  };

  const approveRegionMappingsAccept = async () => {
    let response;

    try {
      setShowSpinner(true);

      response = await approveRegionMappings(approveRegionMappingsIds);

      setShowSpinner(false);
    } catch (error) {
      setShowSpinner(false);
      toast.current.show({
        severity: "error",
        summary: "Approve Failed",
        detail: error.message,
        life: 3000
      });
    }

    setShowSpinner(true);

    const result = await getRegionMappings(currentFilters);
    setRegionMappings(result);
    setSelectedRegionMappings([]);
    setApproveRegionMappingsIds([]);

    setShowSpinner(false);

    if (response?.statusCode === 200) {
      toast.current.show({
        severity: "success",
        summary: "Approved Successfully",
        detail:
          approveRegionMappingsIds.length +
          " region mappings approved successfully.",
        life: 3000
      });
    } else if (response?.statusCode) {
      toast.current.show({
        severity: "error",
        summary: "Approve Failed",
        detail: response.result,
        life: 3000
      });
    }
  };

  const approveRegionMappingsReject = () => {
    setShowApproveRegionMappingsConfirmDialog(false);
    setApproveRegionMappingsIds([]);
  };

  const deleteRegionMappingsHandler = () => {
    setShowSpinner(true);

    if (selectedRegionMappings.length === 0) {
      setShowSpinner(false);

      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please select at least one region mapping to be deleted.",
        life: 3000
      });

      return;
    }

    setDeleteRegionMappingsIds([]);

    selectedRegionMappings.forEach((regionMapping) => {
      setDeleteRegionMappingsIds((prev) => [
        ...prev,
        regionMapping?.regionMappingId
      ]);
    });

    setShowDeleteRegionMappingsConfirmDialog(true);

    setShowSpinner(false);
  };

  const deleteRegionMappingsAccept = async () => {
    let response;

    try {
      setShowSpinner(true);

      response = await deleteRegionMappings(deleteRegionMappingsIds);

      setShowSpinner(false);
    } catch (error) {
      setShowSpinner(false);
      toast.current.show({
        severity: "error",
        summary: "Delete Failed",
        detail: error.message,
        life: 3000
      });
    }

    setShowSpinner(true);

    const result = await getRegionMappings(currentFilters);
    setRegionMappings(result);
    setSelectedRegionMappings([]);
    setDeleteRegionMappingsIds([]);

    setShowSpinner(false);

    if (response?.statusCode === 200) {
      toast.current.show({
        severity: "success",
        summary: "Deleted Successfully",
        detail:
          deleteRegionMappingsIds.length +
          " region mappings deleted successfully.",
        life: 3000
      });
    } else if (response?.statusCode) {
      toast.current.show({
        severity: "error",
        summary: "Delete Failed",
        detail: response.result,
        life: 3000
      });
    }
  };

  const deleteRegionMappingsReject = () => {
    setShowDeleteRegionMappingsConfirmDialog(false);
    setDeleteRegionMappingsIds([]);
  };

  const onShowCreateRegionMappingsHandler = async () => {
    setShowCreateRegionMappingsDialog(true);

    setShowSpinner(true);

    const params = {
      tradingPartner: initialValuesForCreateRegionMapping?.tradingPartner
    };
    await getWalmartRegions({ params });

    setShowSpinner(false);
  };

  const onHideCreateRegionMappingsHandler = async () => {
    setShowCreateRegionMappingsDialog(false);

    setShowSpinner(true);

    const params = {
      tradingPartner: formRef?.current?.values?.tradingPartner
    };
    await getWalmartRegions({ params });

    setShowSpinner(false);
  };

  const onCreateRegionMappingHandler = async (values) => {
    setShowSpinner(true);

    const requiredFields = [
      "tradingPartner",
      "vendorSequenceNumber",
      "regionCode"
    ];
    let hasError = false;

    const newValidationState = { ...validationCheckForCreateRegionMapping };

    requiredFields.forEach((field) => {
      const isEmpty = values?.[field] === null || values?.[field] === "";
      newValidationState[field] = isEmpty;

      if (isEmpty) hasError = true;
    });

    setValidationCheckForCreateRegionMapping(newValidationState);

    if (hasError) {
      setShowSpinner(false);
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please fill all the required fields.",
        life: 3000
      });
      return;
    }

    setValidationCheckForCreateRegionMapping({
      tradingPartner: false,
      vendorSequenceNumber: false,
      regionCode: false
    });

    values = {
      tradingPartner: values?.tradingPartner,
      vendorSequenceNumber: values?.vendorSequenceNumber,
      dcNumber: values?.dcNumber,
      regionCode: values?.regionCode
    };

    try {
      const response = await createRegionMapping(values);

      if (response?.statusCode === 200) {
        setShowCreateRegionMappingsDialog(false);

        const params = {
          tradingPartner: formRef?.current?.values?.tradingPartner
        };
        await getWalmartRegions({ params });

        const result = await getRegionMappings(currentFilters);
        setRegionMappings(result);
        setSelectedRegionMappings([]);

        setShowSpinner(false);

        toast.current.show({
          severity: "success",
          summary: "Created Successfully",
          detail: "Region mapping created successfully.",
          life: 3000
        });
      } else if (response?.statusCode) {
        setShowSpinner(false);
        toast.current.show({
          severity: "error",
          summary: "Creation Failed",
          detail: response.result,
          life: 3000
        });
      }
    } catch (error) {
      setShowSpinner(false);
      toast.current.show({
        severity: "error",
        summary: "Creation Failed",
        detail: error.message,
        life: 3000
      });
    }
  };

  const dataTableFooter = (
    <div className="flex justify-content-start p-3">
      <div className="flex gap-2">
        <Button
          icon="pi pi-verified"
          label="Approve"
          size="small"
          onClick={approveRegionMappingsHandler}
          autoFocus
        />
        <Button
          icon="pi pi-trash"
          label="Delete"
          size="small"
          onClick={deleteRegionMappingsHandler}
          autoFocus
        />
      </div>
    </div>
  );

  return (
    <>
      {showSpinner && <Spinner />}

      <ConfirmDialog
        visible={showApproveRegionMappingsConfirmDialog}
        onHide={approveRegionMappingsReject}
        message={
          approveRegionMappingsIds.length +
          " region mappings will be approved. Do you want to proceed?"
        }
        header="Confirmation"
        icon="pi pi-exclamation-triangle"
        accept={approveRegionMappingsAccept}
        reject={approveRegionMappingsReject}
      />

      <ConfirmDialog
        visible={showDeleteRegionMappingsConfirmDialog}
        onHide={deleteRegionMappingsReject}
        message={
          deleteRegionMappingsIds.length +
          " region mappings will be deleted. Do you want to proceed?"
        }
        header="Confirmation"
        icon="pi pi-exclamation-triangle"
        accept={deleteRegionMappingsAccept}
        reject={deleteRegionMappingsReject}
      />

      <Dialog
        visible={showCreateRegionMappingsDialog}
        modal
        header="Create New Region Mapping"
        style={{ width: "50vw" }}
        breakpoints={{ "1024px": "75vw", "641px": "100vw" }}
        onHide={onHideCreateRegionMappingsHandler}
        maximizable
      >
        <Formik
          initialValues={initialValuesForCreateRegionMapping}
          onSubmit={(values) => {
            setCurrentPage(0);
            setCurrentPageRows(10);
            setShowSpinner(true);
            onCreateRegionMappingHandler(values);
          }}
          innerRef={createRegionMappingFormRef}
        >
          {({ values, handleChange, handleSubmit, setFieldValue }) => (
            <Form onSubmit={handleSubmit} ref={createRegionMappingFormRef}>
              <div>
                <div className="formgrid grid gap-4">
                  <div className="field col">
                    <label
                      htmlFor="tradingPartner"
                      className={styles.formFieldLabel}
                    >
                      Trading Partner
                    </label>
                    <Dropdown
                      id="tradingPartner"
                      filter
                      placeholder="Select a Trading Partner"
                      value={values?.tradingPartner}
                      options={tradingPartnersFilterDropdownList}
                      onChange={async (e) => {
                        setFieldValue("tradingPartner", e.value);
                        setFieldValue("regionCode", null);

                        setShowSpinner(true);
                        const params = {
                          tradingPartner: e.value
                        };
                        await getWalmartRegions({ params });

                        setShowSpinner(false);
                      }}
                      className={`${styles.dropdown} ${styles.formControl}`}
                      invalid={
                        validationCheckForCreateRegionMapping.tradingPartner
                      }
                    />
                  </div>

                  <div className="field col">
                    <label
                      htmlFor="vendorSequenceNumber"
                      className={styles.formFieldLabel}
                    >
                      Vendor Sequence No
                    </label>
                    <InputText
                      id="vendorSequenceNumber"
                      placeholder="Vendor Sequence Number"
                      value={values?.vendorSequenceNumber}
                      onChange={handleChange}
                      className={`${styles.inputField} ${styles.formControl} ${styles.noBorder}`}
                      invalid={
                        validationCheckForCreateRegionMapping.vendorSequenceNumber
                      }
                    />
                  </div>
                </div>

                <div className="formgrid grid gap-4">
                  <div className="field col">
                    <label htmlFor="dcNumber" className={styles.formFieldLabel}>
                      D.C. No
                    </label>
                    <InputText
                      id="dcNumber"
                      placeholder="D.C. Number"
                      value={values?.dcNumber}
                      onChange={handleChange}
                      className={`${styles.inputField} ${styles.formControl} ${styles.noBorder}`}
                    />
                  </div>

                  <div className="field col">
                    <label
                      htmlFor="regionCode"
                      className={styles.formFieldLabel}
                    >
                      Region
                    </label>
                    <Dropdown
                      id="regionCode"
                      filter
                      placeholder="Select a region"
                      value={values?.regionCode}
                      options={regionsFilterDropdownList}
                      onChange={(e) => {
                        setFieldValue("regionCode", e.value);
                      }}
                      className={`${styles.dropdown} ${styles.formControl}`}
                      invalid={validationCheckForCreateRegionMapping.regionCode}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-content-end gap-2 mt-6">
                <Button
                  label="Create"
                  type="submit"
                  size="medium"
                  className={styles.fetchLatestBtn}
                ></Button>

                <Button
                  label="Reset"
                  type="button"
                  size="medium"
                  onClick={async (e) => {
                    setValidationCheckForCreateRegionMapping({
                      tradingPartner: false,
                      vendorSequenceNumber: false,
                      regionCode: false
                    });

                    createRegionMappingFormRef.current.setValues({
                      tradingPartner:
                        tradingPartnersFilterDropdownList[0]?.value,
                      vendorSequenceNumber: "",
                      dcNumber: "",
                      regionCode: null
                    });

                    setShowSpinner(true);

                    const params = {
                      tradingPartner:
                        tradingPartnersFilterDropdownList[0]?.value
                    };
                    await getWalmartRegions({ params });

                    setShowSpinner(false);
                  }}
                  className={styles.resetBtn}
                />
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>

      <Formik
        initialValues={initialValues}
        onSubmit={(values) => {
          setShowSpinner(true);
          fetchRegionMappings(values);
        }}
        innerRef={formRef}
      >
        {({ values, handleChange, handleSubmit, setFieldValue }) => (
          <Form onSubmit={handleSubmit} ref={formRef}>
            <div className="grid">
              <div className="col-12 md:col-2">
                <div className={styles.formLabelContainer}>
                  <label htmlFor="tradingPartner">
                    <span className={styles.formLabel}>Trading Partner</span>
                  </label>
                </div>
                <Dropdown
                  id="tradingPartner"
                  filter
                  placeholder="Select a Trading Partner"
                  value={values?.tradingPartner}
                  options={tradingPartnersFilterDropdownList}
                  onChange={async (e) => {
                    setFieldValue("tradingPartner", e.value);
                    setFieldValue("regionCode", null);

                    setShowSpinner(true);
                    const params = {
                      tradingPartner: e.value
                    };
                    await getWalmartRegions({ params });

                    setCurrentFilters({
                      ...currentFilters,
                      tradingPartner: e.value,
                      regionCode: null
                    });

                    const filterValues = {
                      ...currentFilters,
                      tradingPartner: e.value,
                      regionCode: null
                    };

                    const result = await getRegionMappings(filterValues);
                    setRegionMappings(result);
                    setSelectedRegionMappings([]);

                    setShowSpinner(false);
                  }}
                  className={`${styles.dropdown} ${styles.formControl}`}
                />
              </div>

              <div className="col-12 md:col-2">
                <div className={styles.formLabelContainer}>
                  <label htmlFor="vendorSequenceNumber">
                    <span className={styles.formLabel}>Vendor Sequence No</span>
                  </label>
                </div>
                <div className="p-inputgroup flex-1">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-search"></i>
                  </span>
                  <InputText
                    id="vendorSequenceNumber"
                    placeholder="Vendor Sequence Number"
                    value={values?.vendorSequenceNumber}
                    onChange={handleChange}
                    className={`${styles.inputField} ${styles.formControl} ${styles.noBorder}`}
                  />
                </div>
              </div>

              <div className="col-12 md:col-2">
                <div className={styles.formLabelContainer}>
                  <label htmlFor="dcNumber">
                    <span className={styles.formLabel}>D.C. No</span>
                  </label>
                </div>
                <div className="p-inputgroup flex-1">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-search"></i>
                  </span>
                  <InputText
                    id="dcNumber"
                    placeholder="D.C. Number"
                    value={values?.dcNumber}
                    onChange={handleChange}
                    className={`${styles.inputField} ${styles.formControl} ${styles.noBorder}`}
                  />
                </div>
              </div>

              <div className="col-12 md:col-2">
                <div className={styles.formLabelContainer}>
                  <label htmlFor="regionCode">
                    <span className={styles.formLabel}>Region</span>
                  </label>
                </div>
                <Dropdown
                  id="regionCode"
                  filter
                  placeholder="Select a region"
                  value={values?.regionCode}
                  options={regionsFilterDropdownList}
                  onChange={(e) => {
                    setFieldValue("regionCode", e.value);
                  }}
                  className={`${styles.dropdown} ${styles.formControl}`}
                />
              </div>

              <div className="col-12 md:col-2">
                <div className={styles.formLabelContainer}>
                  <label htmlFor="regionMappingStatus">
                    <span className={styles.formLabel}>Processing Status</span>
                  </label>
                </div>
                <Dropdown
                  id="regionMappingStatus"
                  filter
                  placeholder="Select a Processing Status"
                  value={values?.regionMappingStatus}
                  options={processingStatusFilterDropdownList}
                  onChange={(e) => {
                    setFieldValue("regionMappingStatus", e.value);
                  }}
                  className={`${styles.dropdown} ${styles.formControl}`}
                />
              </div>

              <div className="col-12 md:col-2">
                <div className={styles.formLabelContainer}>
                  <label htmlFor="isAutomated">
                    <span className={styles.formLabel}>Automatic Status</span>
                  </label>
                </div>
                <Dropdown
                  id="isAutomated"
                  filter
                  placeholder="Select a Automatic Status"
                  value={values?.isAutomated}
                  options={automaticStatusFilterDropdownList}
                  onChange={(e) => {
                    setFieldValue("isAutomated", e.value);
                  }}
                  className={`${styles.dropdown} ${styles.formControl}`}
                />
              </div>
            </div>

            <div className="flex justify-content-between">
              <div className="flex align-items-center justify-content-center">
                <Button
                  icon="pi pi-pencil"
                  label="Create Region Mapping"
                  type="button"
                  size="small"
                  onClick={onShowCreateRegionMappingsHandler}
                  autoFocus
                />
              </div>

              <div
                className="flex align-items-center justify-content-center gap-2"
                style={{ marginTop: "1rem", marginBottom: "1rem" }}
              >
                <div className="flex align-items-center justify-content-center">
                  <div className={styles.formLabelContainer}></div>
                  <Button
                    label="Search"
                    type="submit"
                    size="small"
                    className={styles.primaryBtn}
                  />
                </div>
                <div className="flex align-items-center justify-content-center">
                  <div className={styles.formLabelContainer}></div>
                  <Button
                    label="Reset"
                    type="button"
                    size="small"
                    onClick={async (e) => {
                      formRef.current.setValues({
                        tradingPartner:
                          tradingPartnersFilterDropdownList[0]?.value,
                        vendorSequenceNumber: "",
                        dcNumber: "",
                        regionCode: "",
                        regionMappingStatus: processingStatusOptions.Approved,
                        isAutomated: automaticStatusOptions.Manual
                      });

                      setDisableFields(false);

                      setShowSpinner(true);
                      const params = {
                        tradingPartner:
                          tradingPartnersFilterDropdownList[0]?.value
                      };
                      await getWalmartRegions({ params });

                      fetchRegionMappings(initialValues);
                      setShowSpinner(false);
                    }}
                    className={styles.resetBtn}
                  />
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      <DataTable
        value={regionMappings}
        tableStyle={{ minWidth: "50rem" }}
        scrollable
        first={currentPage}
        rows={currentPageRows}
        onPage={(e) =>{setCurrentPage(e.first); setCurrentPageRows(e?.rows)}}
        paginator={constants.paginator}
        rowsPerPageOptions={constants.rowsPerPageOptions}
        paginatorTemplate={constants.paginatorTemplate}
        currentPageReportTemplate={constants.currentPageReportTemplate}
        emptyMessage={constants.emptyMessage}
        dataKey="regionMappingId"
        scrollHeight="30vw"
        selectionMode="checkbox"
        selection={selectedRegionMappings}
        onSelectionChange={(e) => setSelectedRegionMappings(e.value)}
        footer={dataTableFooter}
      >
        <Column
          selectionMode="multiple"
          headerStyle={{ width: "4rem" }}
        ></Column>

        {regionMappingColumns.map((col, index) => (
          <Column
            key={index}
            field={col.field}
            header={col.header}
            sortable={col.sort}
            style={{ minWidth: col.minWidth }}
            headerStyle={{ minWidth: col.minWidth }}
            body={
              col.field.toLocaleLowerCase() === "approveddateutc"
                ? formatAprrovedDateUTCTemplate
                : col.field.toLocaleLowerCase() === "regionselectionstatus"
                ? formatProcessingStatusTemplate
                : col.field.toLocaleLowerCase() === "isautomated"
                ? formatAutomaticStatusTemplate
                : col.value
            }
          />
        ))}
      </DataTable>

      <Toast ref={toast} position="center" />
      <Toast ref={toastTopRight} position="top-right" />
    </>
  );
};

export default RegionMapping;
